/**
 * Palette (semantic color tokens) from Figma (Queen One Design System).
 * Source: palette/Light variable collection.
 * Maps to MUI palette structure. All values reference material-colors.
 * Opacity tokens are pre-computed as color strings; use directly (e.g. backgroundColor, color).
 */

import { materialColors, materialColorsOpacity } from "./material-colors";

const c = materialColors;
const o = materialColorsOpacity;

export const palette = {
  text: {
    primary: c.onyx[600],
    secondary: c.onyx[400],
    contrastText: c.onyx[50],
    disabled: o.onyx["400-40%"],
    _states: {
      filledBackground: o.onyx["400-12%"],
      hover: o.onyx["400-4%"],
      selected: c.onyx[400],
      focus: o.onyx["400-12%"],
      focusVisible: o.onyx["400-24%"],
    },
  },
  primary: {
    main: c.violet[400],
    dark: c.violet[600],
    light: c.violet[300],
    contrastText: c.onyx[50],
    _states: {
      filled: o.violet["50-15%"],
      hover: o.violet["400-4%"],
      selected: o.violet["400-8%"],
      focus: o.violet["400-12%"],
      focusVisible: o.violet["400-24%"],
      neutral: o.onyx["50-40%"],
      outlinedBorder: c.violet[400],
    },
  },
  secondary: {
    main: c.pink[400],
    dark: c.pink[600],
    light: c.pink[300],
    contrastText: c.onyx[50],
    _states: {
      hover: o.pink["300-4%"],
      selected: o.pink["300-8%"],
      focus: o.pink["300-12%"],
      focusVisible: o.pink["300-24%"],
      neutral: o.onyx["50-40%"],
      outlinedBorder: o.pink["300-40%"],
    },
  },
  action: {
    active: c.onyx[300],
    hover: o.onyx["600-4%"],
    selected: o.onyx["600-8%"],
    focus: o.onyx["600-12%"],
    disabled: o.onyx["400-40%"],
    disabledBackground: o.onyx["400-12%"],
  },
  error: {
    main: c.red[400],
    dark: c.red[600],
    light: c.red[100],
    contrastText: c.onyx[50],
    bgContainer: c.red[50],
    _states: {
      hover: o.red["400-4%"],
      selected: o.red["400-8%"],
      focusVisible: o.red["400-24%"],
      outlinedBorder: o.red["400-50%"],
    },
  },
  warning: {
    main: c.amber[400],
    dark: c.amber[600],
    light: c.amber[50],
    contrastText: c.onyx[600],
    bgContainer: c.amber[50],
    _states: {
      hover: o.amber["700-4%"],
      selected: o.amber["700-8%"],
      focusVisible: o.amber["700-24%"],
      outlinedBorder: o.amber["700-50%"],
    },
  },
  info: {
    main: c.blue[400],
    dark: c.blue[600],
    light: c.blue[100],
    contrastText: c.onyx[50],
    bgContainer: c.blue[50],
    _states: {
      hover: o.blue["300-4%"],
      selected: o.blue["300-8%"],
      focusVisible: o.blue["300-24%"],
      outlinedBorder: o.blue["300-50%"],
    },
  },
  success: {
    main: c.green[400],
    dark: c.green[600],
    light: c.green[100],
    contrastText: c.onyx[50],
    bgContainer: c.green[50],
    _states: {
      hover: o.green["200-4%"],
      selected: o.green["200-8%"],
      focusVisible: o.green["200-24%"],
      outlinedBorder: o.green["200-50%"],
    },
  },
  common: {
    black_states: {
      main: c.onyx[500],
      hover: o.onyx["600-4%"],
      selected: o.onyx["600-8%"],
      focus: o.onyx["600-12%"],
      focusVisible: o.onyx["600-24%"],
      outlinedBorder: o.onyx["600-50%"],
    },
    white_states: {
      main: c.onyx[50],
      hover: o.onyx["50-4%"],
      selected: o.onyx["50-8%"],
      focus: o.onyx["50-12%"],
      focusVisible: o.onyx["50-24%"],
      outlinedBorder: o.onyx["50-50%"],
    },
  },
  background: {
    "default-elevation": c.moonlight[50],
    "paper-elevation-0": c.moonlight[100],
    "paper-elevation-1": c.moonlight[200],
    "paper-elevation-2": c.moonlight[300],
    "paper-elevation-3": c.moonlight[400],
    "paper-elevation-4": c.moonlight[500],
    "paper-elevation-5": c.moonlight[600],
    "paper-elevation-6": c.moonlight[700],
    "paper-elevation-7": c.moonlight[800],
    "paper-elevation-8": c.moonlight[900],
    "paper-elevation-9": c.moonlight[1000],
    "paper-elevation-10": c.moonlight[1100],
    "paper-elevation-11": c.moonlight[1200],
    "paper-elevation-12": c.moonlight[1300],
    "paper-elevation-reversed": c.moonlight[1400],
    "paper-depression": c.moonlight[1500],
    shround: o.moonlight["1400-80%"],
  },
  outlines: {
    defaultDividerLight: c.onyx[100],
    defaultDividerDark: c.onyx[300],
    defaultDividerWhite: c.onyx[50],
    defaultBordersLight: c.onyx[100],
    defaultBordersBalanced: c.onyx[200],
    defaultBordersDark: c.onyx[300],
  },
  elevation: {
    outlineDark: c.onyx[300],
    outlineLight: c.onyx[300],
  },
  _components: {
    avatar: { fill: c.onyx[200] },
    input: {
      standard: {
        enabledBorder: c.onyx[300],
        hoverBorder: c.onyx[600],
        activeBorder: c.violet[400],
      },
      filled: {
        enabledFill: c.onyx[100],
        hoverFill: c.onyx[600],
        activeBorder: c.violet[50],
      },
      outlined: {
        enabledBorder: c.onyx[300],
        hoverBorder: c.onyx[600],
        activeBorder: c.violet[400],
      },
    },
    switch: {
      knobFillEnabled: c.onyx[50],
      knowFillDisabled: c.onyx[200],
      slideDefault: c.onyx[500],
      slideDisabled: c.onyx[100],
      slideEnabledPrimary: c.violet[400],
      slideEnabledSecondary: c.pink[400],
      slideSuccess: c.green[400],
      slideError: c.red[400],
      slideWarning: c.amber[400],
      slideInfo: c.blue[400],
    },
    rating: {
      enabledBorder: o.onyx["600-24%"],
      activeFill: c.amber[400],
    },
    snackbar: { fill: c.onyx[400] },
    chip: {
      defaultCloseFill: c.onyx[600],
      defaultHoverFill: o.onyx["600-12%"],
      defaultEnabledBorder: c.onyx[400],
      defaultFocusFill: o.onyx["600-24%"],
    },
    tooltip: { fill: c.onyx[500] },
    backdrop: { fill: o.violet["200-24%"] },
    appBar: { defaultFill: c.onyx[100] },
    breadcrumbs: { collapseFill: c.onyx[100] },
    alert: {
      error: { color: c.red[50], background: c.red[400] },
      warning: { color: c.amber[50], background: c.amber[400] },
      info: { color: c.onyx[100], background: c.blue[400] },
      success: { color: c.green[50], background: c.green[400] },
    },
    stepper: { connector: c.onyx[400] },
  },
  _native: {
    "scrollbar-bg": c.onyx[200],
  },
  icon: {
    default: c.onyx[600],
    hover: c.violet[200],
    active: c.violet[400],
    disabled: o.onyx["400-12%"],
    contrastIcon: c.onyx[50],
  },
} as const;
